import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { apiClient, getAuthToken, setAuthToken, clearAuthToken, API as RESOLVED_API } from "../lib/api";
import { forgetAuthToken, isTokenClearedEvent, replaceAuthToken, shouldRenewToken } from "../lib/authToken";
import { invalidateFetes } from "../lib/fetes";
import { clearAllDrafts } from "../lib/poche";

// 🛡 B6 — Utiliser l'API de lib/api.js (qui a un fallback Railway hardcodé)
// pour éviter "BACKEND_URL = undefined" si la variable d'env Vercel n'est
// pas posée. Avant : ces requêtes tombaient sur "undefined/api/..." → Network
// Error opaque côté login.
export const API = RESOLVED_API;

const AuthContext = createContext(null);

const POLL_INTERVAL = 60_000; // check every 60 s
// Au plus une tentative de renouvellement par heure et par onglet : un échec
// (réseau, 429) ne tourne jamais en boucle au rythme du sondage.
const RENEW_RETRY_MS = 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kickedOut, setKickedOut] = useState(false);
  const pollRef = useRef(null);
  const lastRenewRef = useRef(0);
  const renewAbortRef = useRef(null);

  // Coupe un renouvellement en vol. Sa réponse repose les cookies de l'API :
  // arrivée APRÈS la déconnexion (ou après la connexion d'un autre compte),
  // elle reconnecterait l'ancien compte au prochain chargement. Annulée
  // avant la requête suivante, sa réponse est ignorée par le navigateur.
  const cancelRenew = useCallback(() => {
    if (renewAbortRef.current) {
      renewAbortRef.current.abort();
      renewAbortRef.current = null;
    }
  }, []);

  // Clears local state and flags the reason. Also wipes the bearer token so
  // a subsequent reload doesn't keep authenticating with a stale credential.
  const forceLogout = useCallback((reason = "session") => {
    cancelRenew();
    setUser(null);
    clearAuthToken();
    invalidateFetes(); // le compte suivant ne doit pas voir les fêtes de celui-ci
    // Brouillons de la Poche (noms et montants de donateurs) : effacés à la
    // déconnexion. PAS sur un simple 401 de /auth/me (session expirée) : la
    // dictée en cours doit survivre à une reconnexion.
    clearAllDrafts();
    if (reason === "disabled") setKickedOut(true);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, [cancelRenew]);

  // Renouvellement de session : si le jeton gardé a plus de 24 h, le serveur
  // en émet un neuf de 7 jours (POST /auth/renew, mêmes contrôles que
  // /auth/me : mot de passe changé ou compte désactivé → refusé). Tant que
  // l'app est ouverte au moins une fois par semaine, on ne redemande plus le
  // mot de passe. Échec silencieux : le jeton actuel reste valable jusqu'à
  // son expiration. Sans jeton gardé (cookie seul), rien à renouveler ici.
  // Tenté après chaque /auth/me réussi (démarrage, sondage, retour dans
  // l'app) : iOS reprend souvent la web app SANS la recharger, un essai au
  // seul chargement ne suffirait pas. Jeton neuf → plus rien pendant 24 h.
  const renewIfOld = useCallback(async () => {
    const current = getAuthToken();
    if (!current || !shouldRenewToken(current)) return;
    if (renewAbortRef.current) return; // déjà en vol
    const now = Date.now();
    if (now - lastRenewRef.current < RENEW_RETRY_MS) return;
    lastRenewRef.current = now;
    const controller = new AbortController();
    renewAbortRef.current = controller;
    try {
      // Le serveur ne lit QUE le Bearer (jamais le cookie) : on renouvelle
      // exactement le jeton gardé ici.
      const res = await apiClient.post("/auth/renew", null, { signal: controller.signal });
      // Remplacé seulement là où l'ancien est encore gardé : déconnexion (ici
      // ou dans un autre onglet) ou changement de compte pendant l'appel →
      // rien n'est réécrit.
      replaceAuthToken(current, res.data?.token);
    } catch {
      /* silencieux (annulé, 401, 429, réseau) : nouvel essai dans une heure au plus tôt */
    } finally {
      if (renewAbortRef.current === controller) renewAbortRef.current = null;
    }
  }, []);

  // Use apiClient (not axios direct) so the Bearer interceptor fires. Critical
  // for iOS Safari where cross-site cookies are dropped silently — without
  // this, /auth/me always returns 401 on mobile and the dashboard explodes.
  const checkAuth = useCallback(async () => {
    const sent = getAuthToken(); // celui que l'intercepteur va présenter
    try {
      const res = await apiClient.get("/auth/me");
      setUser(res.data);
      setKickedOut(false);
      renewIfOld(); // ne fait rien si le jeton a moins de 24 h (cf. plus haut)
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 403 && /compte\s+d[ée]sactiv[ée]/i.test(detail)) {
        forceLogout("disabled");
      } else {
        // Jeton périmé/révoqué : on l'efface partout où il est gardé (les deux
        // copies) pour ne pas le représenter à chaque requête. Seulement lui :
        // un jeton neuf rangé entre-temps par un autre onglet reste.
        if (err?.response?.status === 401) forgetAuthToken(sent);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [forceLogout, renewIfOld]);

  // Déconnexion dans un autre onglet (copie persistante effacée) : cet onglet
  // suit, sinon il garderait la session et la relancerait au prochain
  // renouvellement.
  useEffect(() => {
    const onStorage = (event) => {
      if (isTokenClearedEvent(event, getAuthToken())) forceLogout("session");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [forceLogout]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Retour dans l'app (web app reprise depuis l'arrière-plan, onglet
  // réaffiché) : renouvellement tout de suite si le jeton a vieilli, sans
  // attendre le sondage — une ouverture de quelques secondes suffit. Aucun
  // appel réseau si le jeton a moins de 24 h. Seulement si connecté (le
  // sondage tourne) ; un jeton révoqué entre-temps est refusé par le serveur.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && pollRef.current) renewIfOld();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [renewIfOld]);

  // Periodic polling: re-checks auth every 60 s while logged in
  useEffect(() => {
    if (!user) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    pollRef.current = setInterval(checkAuth, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user, checkAuth]);

  const loginWithPassword = async (email, password) => {
    cancelRenew();
    const res = await axios.post(`${API}/auth/login`, { email, password }, {
      withCredentials: true,
    });
    // Double authentification : mot de passe OK mais code Google Authenticator
    // requis → on ne connecte PAS encore (le front appelle completeTwofa).
    if (res.data?.requires_2fa) {
      return res.data;
    }
    // Garde le jeton (sessionStorage + copie persistante, cf. lib/authToken.js)
    // pour que l'intercepteur d'apiClient envoie `Authorization: Bearer ...`
    // sur iPhone, où Safari peut ignorer le cookie httpOnly de l'API. Le
    // cookie reste posé par la réponse pour les navigateurs qui l'acceptent.
    if (res.data?.token) {
      setAuthToken(res.data.token);
    }
    setUser(res.data.user);
    setKickedOut(false);
    return res.data;
  };

  // 2ᵉ étape 2FA : échange le jeton court + code à 6 chiffres contre la
  // vraie session (mêmes effets que loginWithPassword).
  const completeTwofa = async (twofaToken, code) => {
    cancelRenew();
    const res = await axios.post(
      `${API}/auth/2fa/complete`,
      { twofa_token: twofaToken, code },
      { withCredentials: true },
    );
    if (res.data?.token) {
      setAuthToken(res.data.token);
    }
    setUser(res.data.user);
    setKickedOut(false);
    return res.data;
  };

  // « Se connecter avec Google » : le bouton officiel renvoie un ID token
  // (credential) → vérifié côté serveur → mêmes effets qu'un login classique.
  const loginWithGoogle = async (credential) => {
    cancelRenew();
    const res = await axios.post(
      `${API}/auth/google/verify`,
      { credential },
      { withCredentials: true },
    );
    // 2FA active sur le compte : pas encore de session — le front affiche
    // l'étape « code » (même flux que loginWithPassword).
    if (res.data?.requires_2fa) {
      return res.data;
    }
    if (res.data?.token) {
      setAuthToken(res.data.token);
    }
    setUser(res.data.user);
    setKickedOut(false);
    return res.data;
  };

  const logout = async () => {
    // AVANT l'appel réseau :
    //  - la réponse d'un renouvellement en vol ne doit pas reposer les
    //    cookies derrière celle de /auth/logout, qui les efface ;
    //  - le jeton gardé (copie persistante comprise) est effacé tout de
    //    suite : réseau lent + app fermée dans la foulée ne doit pas laisser
    //    une session vivante au prochain lancement. /auth/logout n'en a pas
    //    besoin (il n'efface que les cookies).
    cancelRenew();
    clearAuthToken();
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    forceLogout("manual");
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, kickedOut, checkAuth, loginWithPassword, completeTwofa, loginWithGoogle, logout, forceLogout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
